import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Joi from 'joi';

import { useSendSMSMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { RoleNames } from 'utils/constants';
import { validateSchema, parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        padding: theme.spacing(2, 6),
    },
    messageContent: {
        minHeight: 150,
        height: 'auto',
    },
}));

const SMSDialog = props => {
    const { open, handleClose, receiver } = props;
    const classes = useStyles();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const [sendSMS, { isLoading: loading, isSuccess }] = useSendSMSMutation();

    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);

    const companyName = useMemo(
        () => internalUser.distributorCompany?.corporateName || '',
        [internalUser]
    );

    const sender = useMemo(() => {
        if (userIsDistributor) {
            return companyName;
        } else {
            return texts.PEQAN_TEAM;
        }
    }, [userIsDistributor, companyName]);

    const defaultMessage = useMemo(
        () =>
            `${receiver?.title || texts.CHERE} ${parseFullName(
                receiver
            )},\n\nCordialement,\n${sender}`,
        [receiver, sender]
    );

    const [message, setMessage] = useState(defaultMessage + ``);
    const [errors, setErrors] = useState({ message: '' });

    const schema = useMemo(
        () => ({
            message: Joi.string().required().invalid(defaultMessage).messages({
                'string.empty': texts.VEUILLES_SAISIR_UN_MESSAGE,
                'any.invalid': texts.VEUILLES_SAISIR_UN_MESSAGE,
            }),
        }),
        [defaultMessage]
    );

    const resetNotificationContent = useCallback(() => {
        setMessage(defaultMessage);
        setErrors({ message: '' });
    }, [defaultMessage]);

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, { message });
        setErrors(newErrors);

        return valid;
    }, [schema, message]);

    const handleSendNotification = useCallback(() => {
        if (formIsValid()) {
            sendSMS({ receiver: receiver?.id, message });
        }
    }, [formIsValid, message, sendSMS, receiver]);

    const onClosePopUp = useCallback(() => {
        handleClose();
        resetNotificationContent();
    }, [handleClose, resetNotificationContent]);

    useEffect(() => {
        if (isSuccess) {
            onClosePopUp();
        }
    }, [isSuccess, onClosePopUp]);

    useEffect(() => {
        if (defaultMessage) setMessage(defaultMessage);
    }, [defaultMessage]);

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <CustomDialogTitle title={texts.ENVOYER_UN_SMS} handleClose={onClosePopUp} />
            <DialogContent className={classes.dialogContent}>
                <CustomTextField
                    fullWidth
                    multiline
                    classes={{ root: classes.messageContent }}
                    minRows={6}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    error={errors.message}
                />
            </DialogContent>
            <DialogActions>
                <ActionButton variant="text" disabled={loading} onClick={handleSendNotification}>
                    {texts.ENVOYER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

SMSDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    receiver: PropTypes.object,
};

export default SMSDialog;
